import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// ⚠️ توجه: ایمپورت لوگوها کامنت شد. در پروژه محلی خود، این خطوط را از کامنت خارج کنید
// و آدرس src تصویر را در JSX به‌روزرسانی کنید.
 import whiteLogo from '../images/monocolorlogo.png';
 import colorfulLogo from '../images/colorfullogo.png';


// --- کامپوننت صفحه لاگین ---
const LoginPage = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true);

  const location = useLocation();
  const navigate = useNavigate();

  // استایل‌های پویا بر اساس حالت تاریک/روشن
  const styles = useMemo(() => ({
    section: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
      minHeight: '100vh',
      background: isDarkMode
        ? 'linear-gradient(to top right, rgba(19, 7, 79, 0.5), transparent, transparent), #111827'
        : 'linear-gradient(to top right, rgba(242, 244, 247, 0.8), transparent, transparent), #f3f4f6',
      transition: 'background 0.7s ease',
      position: 'relative',
    },
    container: {
      borderRadius: '8px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      padding: '32px',
      width: '100%',
      maxWidth: '400px',
      backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
      transition: 'background-color 0.7s ease',
    },
    logo: {
      height: '64px',
      width: 'auto',
      display: 'block',
      margin: '0 auto 24px',
    },
    title: {
      fontSize: '24px',
      fontWeight: 'bold',
      textAlign: 'center',
      color: isDarkMode ? '#ffffff' : '#000000',
      marginBottom: '24px',
    },
    errorMessage: {
      color: '#dc2626',
      textAlign: 'center',
      marginBottom: '16px',
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    },
    label: {
      fontSize: '14px',
      fontWeight: '500',
      color: isDarkMode ? '#ffffff' : '#000000',
    },
    input: {
      width: '100%',
      padding: '8px 0px',
      border: isDarkMode ? '1px solid #4b5563' : '1px solid #d1d5db',
      borderRadius: '4px',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
      outline: 'none',
      fontSize: '16px',
      backgroundColor: isDarkMode ? '#374151' : '#f9fafb',
      color: isDarkMode ? '#ffffff' : '#000000',
      textIndent: '8px',
    },
    inputFocus: {
      borderColor: '#2563eb',
      boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
    },
    button: {
      width: '100%',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      padding: '8px 16px',
      border: 'none',
      borderRadius: '4px',
      fontSize: '16px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    buttonHover: {
      backgroundColor: '#1d4ed8',
    },
    buttonDisabled: {
      backgroundColor: '#4b5563',
      cursor: 'not-allowed',
    },
    externalButton: {
      width: '100%',
      backgroundColor: '#16a34a', // green
      color: '#ffffff',
      padding: '8px 16px',
      border: 'none',
      borderRadius: '4px',
      fontSize: '16px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      marginTop: '20px',
    },
    externalButtonHover: {
      backgroundColor: '#15803d', // darker green
    },
    toggleButton: {
      position: 'absolute',
      top: '24px',
      right: '24px',
      background: isDarkMode ? '#4b5563' : '#e5e7eb',
      color: isDarkMode ? '#ffffff' : '#000000',
      border: 'none',
      borderRadius: '50%',
      width: '40px',
      height: '40px',
      cursor: 'pointer',
      fontSize: '18px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10,
    },
  }), [isDarkMode]);

  // تابع یکپارچه برای لاگین
  const performLogin = useCallback(async (user, pass) => {
    if (loading) return;
    setLoading(true);
    setMessage('');

    try {
      const response = await fetch('https://sguardapi.tabangohar.com/api/login.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username: user, password: pass }),
      });

      const data = await response.json();

      if (response.ok && data.status === 'success') {
        onLogin(data.token); // تابع onLogin از App.jsx را فراخوانی می‌کند
      } else {
        setMessage(data.message || 'خطای ناشناخته');
      }
    } catch (error) {
      setMessage('خطا در ارتباط با سرور');
      console.error('Login error:', error);
    } finally {
      setLoading(false);
      // پارامترهای GET را از URL پاک می‌کنیم
      navigate('/login', { replace: true });
    }
  }, [loading, navigate, onLogin]);

  // متد ۱: بررسی پارامترهای URL (لاگین GET)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const user = searchParams.get('user');
    const pass = searchParams.get('password');

    if (user && pass) {
      setMessage('در حال ورود از طریق پارامتر... (این روش ناامن است)');
      performLogin(user, pass);
    }
  }, [location, performLogin]);

  // متد ۲: لاگین از طریق فرم (POST)
  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!username || !password) {
      setMessage('لطفاً نام کاربری و رمز عبور را وارد کنید.');
      return;
    }
    performLogin(username, password);
  };

  return (
    <section style={styles.section}>
      <button
        style={styles.toggleButton}
        onClick={() => setIsDarkMode(!isDarkMode)}
      >
        {isDarkMode ? '☀️' : '🌙'}
      </button>

      <div style={styles.container}>
        <img
          id="logoSignInForm"
          // [اصلاح شد] استفاده از لوگوی جایگزین برای رفع خطای کامپایل
          // در پروژه محلی، این خط را با: src={isDarkMode ? whiteLogo : colorfulLogo} جایگزین کنید
          src={isDarkMode 
            ? whiteLogo 
            : colorfulLogo
          }
          alt="لوگو"
          style={styles.logo}
        />
        <h1 style={styles.title}>صفحه ورود</h1>
        {message && <p style={styles.errorMessage}>{message}</p>}

        <form onSubmit={handleFormSubmit} style={styles.form}>
          <div>
            <label htmlFor="username" style={styles.label}>
              نام کاربری:
            </label>
            <input
              type="text"
              id="username"
              value={username}
              // [اصلاح باگ] e.g.value به e.target.value تغییر کرد
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="off"
              style={styles.input}
            />
          </div>
          <div>
            <label htmlFor="password" style={styles.label}>
              رمز عبور:
            </label>
            <div className="password-input-container">
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={styles.input}
              />
            </div>
          </div>
          <button
            id="login"
            type="submit"
            disabled={loading}
            style={{
              ...styles.button,
              ...(loading ? styles.buttonDisabled : {})
            }}
            onMouseOver={(e) => !loading && (e.target.style.backgroundColor = styles.buttonHover.backgroundColor)}
            onMouseOut={(e) => !loading && (e.target.style.backgroundColor = styles.button.backgroundColor)}
          >
            {loading ? 'در حال ورود...' : 'ورود'}
          </button>
        </form>
        <div>
          <button
            onClick={() =>
              (window.location.href =
                'https://tgnsys.ir/login?redirect=https://sguardapi.tabangohar.com/login')
            }
            disabled={loading}
            style={{
              ...styles.externalButton,
              ...(loading ? styles.buttonDisabled : {})
            }}
            onMouseOver={(e) => !loading && (e.target.style.backgroundColor = styles.externalButtonHover.backgroundColor)}
            onMouseOut={(e) => !loading && (e.target.style.backgroundColor = styles.externalButton.backgroundColor)}
          >
            ورود از طریق tgnsys.ir
          </button>
        </div>
      </div>
    </section>
  );
};

export default LoginPage;